import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Line, Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { BeatLoader } from "react-spinners";
import "../../../../../styles/count_graphs.css";
import { getPolygonBorderColor } from "./DwellTimeUtils";
import {
  BiFullscreen,
  BiExitFullscreen,
  BiLineChart,
  BiBarChartAlt2,
} from "react-icons/bi";
import { peopleDwellColor } from "../../../../../config";

const DwellChart = ({
  selectedYear,
  weeklyDwellData,
  weeklyDwellDataLoading,
  selectedWeek,
  selectedWeekOfMonth,
  selectedMonthName,
  setSelectedWeek,
  weeklyAverage,
  weeklyIncrease,
  isLineChart,
  toggleLineChart,
}) => {
  const [chartData, setChartData] = useState();

  const smallScreen = window.innerWidth < 786;
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Assuming that the weekly dwell data is just a list rather than a list of maps.
  const prepareWeeklyDwellChart = () => {
    // console.log("\n=== PREPARING WEEKLY CHART ===");
    // console.log("weeklyDwellData received:", weeklyDwellData);

    // Handle empty/invalid data by returning empty chart structure
    if (
      !weeklyDwellData ||
      !Array.isArray(weeklyDwellData) ||
      weeklyDwellData.length === 0
    ) {
      console.warn("No weekly dwell data available");
      return {
        labels: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"],
        datasets: [],
      };
    }

    // Log each week's content
    weeklyDwellData.forEach((week, i) => {
      // console.log(`Week ${i + 1}:`, week, `(${week?.length || 0} items)`);
    });

    const weekLabels = weeklyDwellData.map((_, i) => `Week ${i + 1}`);
    // console.log("Created week labels:", weekLabels);

    // IMPROVED: Handle empty weeks better
    const polygonLabels = new Map();

    weeklyDwellData.forEach((week, weekIndex) => {
      if (week && Array.isArray(week)) {
        week.forEach((polygon) => {
          if (polygon && polygon.polygon_id && polygon.polygon_name) {
            // console.log(
            //   `Found polygon in week ${weekIndex + 1}:`,
            //   polygon.polygon_name,
            //   "ID:",
            //   polygon.polygon_id,
            //   "Dwell:",
            //   polygon.average_dwell_time
            // );
            polygonLabels.set(polygon.polygon_id, polygon.polygon_name);
          } else {
            console.warn(
              `Invalid polygon data in week ${weekIndex + 1}:`,
              polygon
            );
          }
        });
      } else {
        // console.log(`Week ${weekIndex + 1} is empty or invalid:`, week);
      }
    });

    // console.log("Found unique polygons:", Array.from(polygonLabels.entries()));

    // IMPORTANT: Check if we found any polygons at all
    if (polygonLabels.size === 0) {
      console.warn(
        "No valid polygons found in any week - this might be why chart is empty"
      );
      // Return empty chart but with proper structure
      return {
        labels: weekLabels,
        datasets: [],
      };
    }

    const datasets = () => {
      const result = [];

      for (const [polygon_id, polygon_name] of polygonLabels) {
        // console.log(
        //   `Creating dataset for polygon: ${polygon_name} (${polygon_id})`
        // );

        const dataPoints = weeklyDwellData.map((week, weekIndex) => {
          if (!week || !Array.isArray(week)) {
            // console.log(`Week ${weekIndex + 1} - no data (empty/invalid week)`);
            return 0;
          }

          const polygon = week.find((p) => p && p.polygon_id === polygon_id);
          if (polygon && typeof polygon.average_dwell_time === "number") {
            const minutes = (polygon.average_dwell_time / 60).toFixed(2);
            // console.log(
            //   `Week ${weekIndex + 1}, ${polygon_name}: ${
            //     polygon.average_dwell_time
            //   }s = ${minutes} minutes`
            // );
            return parseFloat(minutes);
          } else {
            // console.log(`Week ${weekIndex + 1}, ${polygon_name}: no data`);
            return 0;
          }
        });

        // console.log(`Final data points for ${polygon_name}:`, dataPoints);

        result.push({
          label: polygon_name,
          data: dataPoints,
          backgroundColor: getPolygonBorderColor(polygon_id),
          borderColor: getPolygonBorderColor(polygon_id),
          borderWidth: 3,
          tension: 0.3,
        });
      }

      // console.log(`Created ${result.length} datasets`);
      return result;
    };

    const finalChartData = {
      labels: weekLabels,
      datasets: datasets(),
    };

    // console.log("=== FINAL WEEKLY CHART DATA ===");
    // console.log("Labels:", finalChartData.labels);
    // console.log("Datasets count:", finalChartData.datasets.length);
    // console.log("Datasets:", finalChartData.datasets);

    // CRITICAL: Check if we have any actual data
    const hasAnyData = finalChartData.datasets.some((dataset) =>
      dataset.data.some((value) => value > 0)
    );
    // console.log("Chart has actual data points:", hasAnyData);

    if (!hasAnyData) {
      console.warn(
        "⚠️  Chart has no data points > 0 - this explains why chart appears empty"
      );
    }

    return finalChartData;
  };

  useEffect(() => {
    const preparedChart = prepareWeeklyDwellChart();
    if (preparedChart) {
      setChartData(preparedChart);
    }
  }, [weeklyDwellData]);

  const handleWeekSectionClick = (chart, event) => {
    // console.log("Week section clicked");
    const nativeEvent = event.native || event;

    const rect = chart.canvas.getBoundingClientRect();
    let x;

    if (nativeEvent.clientX !== undefined) {
      x = nativeEvent.clientX - rect.left;
    } else if (event.x !== undefined) {
      x = event.x - rect.left;
    } else {
      const position = chart.getRelativePosition(event, chart);
      x = position.x;
    }

    const chartArea = chart.chartArea;

    if (x >= chartArea.left && x <= chartArea.right) {
      const weekWidth =
        (chartArea.right - chartArea.left) / chart.data.labels.length;
      const weekIndex = Math.floor((x - chartArea.left) / weekWidth);

      // console.log(`Clicked on week index: ${weekIndex}`);
      // console.log(`Available weeks:`, weeklyDwellData);

      // Validate weekIndex and data
      if (
        weekIndex >= 0 &&
        weekIndex < weeklyDwellData.length &&
        weeklyDwellData[weekIndex]
      ) {
        if (weeklyDwellData[weekIndex].length > 0) {
          const selectedWeekData = weeklyDwellData[weekIndex][0];
          // console.log("Setting selected week to:", selectedWeekData.month_week);
          setSelectedWeek(selectedWeekData.month_week);
        } else {
          console.warn("Clicked on a week with no data");
        }
      } else {
        console.warn("Invalid week index or no data available");
      }
    }
  };

  const handleChartHover = (event) => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const rect = chart.canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;

      // Store the x position for use in the plugin
      chart.hoverX = event.clientX - rect.left;

      // Store the x position for use in the plugin
      chart.hoverX = x;

      // Check if we're within the chart area
      const { chartArea } = chart;
      if (x >= chartArea.left && x <= chartArea.right) {
        // We're hovering over a month section, change cursor to pointer
        event.target.style.cursor = "pointer";
      } else {
        // Not over a month section, use default cursor
        event.target.style.cursor = "default";
      }

      // Trigger a redraw to show the highlight
      chart.update("none"); // 'none' mode for fast updates
    }
  };

  // Add this function to clear highlight when mouse leaves
  const handleChartMouseLeave = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      chart.hoverX = null;
      chart.canvas.style.cursor = "default";
      chart.update("none");
    }
  };
  Chart.register(...registerables);

  return (
    <div className="daily-bar-chart-container">
      <div
        className={`count-bar-chart-wrapper ${
          isFullscreen ? "fullscreen-container" : ""
        }`}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          {/* Title */}
          <div
            className="graph-title"
            style={{ flex: "1 1 auto", minWidth: "200px" }}
          >
            Weekly Average Dwell Time
          </div>

          {/* Controls and Percentage */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              justifyContent: "flex-end",
              gap: "1rem",
              flex: "1 1 auto",
              minWidth: "100px",
            }}
          >
            <button
              className="toggle-chart-button"
              onClick={() => toggleLineChart("week")}
            >
              {isLineChart ? (
                <BiBarChartAlt2
                  size={24}
                  title="Switch to Bar Chart"
                  color="white"
                />
              ) : (
                <BiLineChart
                  size={24}
                  title="Switch to Line Chart"
                  color="white"
                />
              )}
            </button>
            <button
              className="toggle-chart-button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <BiExitFullscreen size={24} color="white" />
              ) : (
                <BiFullscreen size={24} color="white" />
              )}
            </button>
          </div>
        </div>

        <div
          className={
            weeklyDwellDataLoading
              ? "dwell-daily-bar-chart-container-loading"
              : ""
          }
        >
          <div className="chart-total-container">
            <div className="chart-total-pill">
              <div
                className="chart-total-label"
                style={{ color: peopleDwellColor }}
              >
                {" "}
                Avg Weekly Dwell (min):{" "}
                {weeklyDwellData && weeklyDwellData.length > 0
                  ? (() => {
                      const flatData = weeklyDwellData
                        .flat()
                        .filter((item) => item?.average_dwell_time != null);
                      const total = flatData.reduce(
                        (sum, item) => sum + item.average_dwell_time,
                        0
                      );
                      const avg =
                        flatData.length > 0
                          ? (total / flatData.length / 60).toFixed(1)
                          : "0.0";
                      return avg;
                    })()
                  : "0.0"}
              </div>
            </div>
          </div>
          {isLineChart ? (
            <Line
              ref={chartRef}
              data={chartData || { labels: [], datasets: [] }} // Fetch prepared data
              options={{
                responsive: true, // Ensure the chart is responsive
                maintainAspectRatio: true,
                hover: {
                  mode: "nearest",
                  intersect: false,
                }, // Ensure the chart maintains its aspect ratio
                scales: {
                  x: {
                    ticks: {
                      color: (context) => {
                        // Change this to accomodate multiple polygons per week.
                        return context.tick.label === `Week ${selectedWeek}`
                          ? "#32de84"
                          : "#F8F8F8";
                      },
                      font: {
                        weight: (context) =>
                          context.tick.label === `Week ${selectedWeek}`
                            ? "bold"
                            : "normal",
                        size: (context) =>
                          context.tick.label === `Week ${selectedWeek}`
                            ? 16
                            : 14,
                      },
                    },
                    title: {
                      display: true,
                      text: "Weeks of the month",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                    grid: {
                      display: true,
                      drawBorder: true,
                      drawOnChartArea: false,
                      color: "#aaa",
                    },
                  },
                  y: {
                    ticks: {
                      color: "#F8F8F8",
                    },
                    title: {
                      display: true,
                      text: "Minutes",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    mode: "nearest", // Show tooltip for all datasets at the same index
                  },
                  legend: {
                    position: "top", // Position the legend at the top
                    labels: {
                      color: "white",
                      font: {
                        size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                      },
                      boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
                      boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                  },
                  weekHighlight: {
                    highlightColor: "rgba(50, 222, 132, 0.2",
                  },
                },

                onClick: (event, elements) => {
                  // console.log("chart clicked!!!");
                  const chart = chartRef.current;
                  if (chart) {
                    handleWeekSectionClick(chart, event);
                  }
                },
                onHover: function (event, elements) {
                  const chartElement = this.canvas;
                  if (elements && elements.length > 0) {
                    chartElement.style.cursor = "pointer";
                  } else {
                    chartElement.style.cursor = "default";
                  }
                },
              }}
            />
          ) : (
            <Bar
              ref={chartRef}
              data={chartData || { labels: [], datasets: [] }} // Fetch prepared data
              options={{
                responsive: true, // Ensure the chart is responsive
                maintainAspectRatio: true,
                hover: {
                  mode: "nearest",
                  intersect: false,
                },
                onHover: function (event, elements) {
                  const chartElement = this.canvas;
                  if (elements && elements.length > 0) {
                    chartElement.style.cursor = "pointer";
                  } else {
                    chartElement.style.cursor = "default";
                  }
                },
                scales: {
                  x: {
                    ticks: {
                      color: (context) => {
                        // Change this to accomodate multiple polygons per week.
                        return context.tick.label === `Week ${selectedWeek}`
                          ? "#32de84"
                          : "#F8F8F8";
                      },
                      font: {
                        weight: (context) =>
                          context.tick.label === `Week ${selectedWeek}`
                            ? "bold"
                            : "normal",
                        size: (context) =>
                          context.tick.label === `Week ${selectedWeek}`
                            ? 16
                            : 14,
                      },
                    },
                    title: {
                      display: true,
                      text: "Weeks of the month",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                    grid: {
                      display: true,
                      drawBorder: true,
                      drawOnChartArea: false,
                      color: "#aaa",
                    },
                  },
                  y: {
                    ticks: {
                      color: "#F8F8F8",
                    },
                    title: {
                      display: true,
                      text: "Minutes",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    mode: "nearest", // Show tooltip for all datasets at the same index
                  },
                  legend: {
                    position: "top", // Position the legend at the top
                    labels: {
                      color: "white",
                      font: {
                        size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                      },
                      boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
                      boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                  },
                  weekHighlight: {
                    highlightColor: "rgba(50, 222, 132, 0.2",
                  },
                },

                onClick: (event, elements) => {
                  // console.log("chart clicked!!!");
                  const chart = chartRef.current;
                  if (chart) {
                    handleWeekSectionClick(chart, event);
                  }
                },
              }}
            />
          )}
          {/* <div className='dwell-chart-footer-container'>
                        <div className='dwell-chart-footer-label'>Weekly average dwell time for  week {selectedWeekOfMonth} of {selectedMonthName} {selectedYear}:</div>
                        <div className='dwell-chart-footer-value'>{weeklyAverage}</div>
                    </div> */}
          {weeklyDwellDataLoading && (
            <div id="dwell-week-loading-overlay">
              <BeatLoader
                color={"white"}
                loading={weeklyDwellDataLoading}
                size={15}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(DwellChart, (prevProps, nextProps) => {
  return (
    prevProps.selectedYear === nextProps.selectedYear &&
    prevProps.selectedWeek === nextProps.selectedWeek &&
    prevProps.selectedWeekOfMonth === nextProps.selectedWeekOfMonth &&
    prevProps.selectedMonthName === nextProps.selectedMonthName &&
    prevProps.weeklyDwellDataLoading === nextProps.weeklyDwellDataLoading &&
    prevProps.isWeeklyLineChart === nextProps.isWeeklyLineChart &&
    prevProps.weeklyAverage === nextProps.weeklyAverage &&
    prevProps.weeklyIncrease === nextProps.weeklyIncrease &&
    prevProps.weeklyDwellData === nextProps.weeklyDwellData
  );
});
